import {
  getMinimumBorrowAmount,
  getMinimumDepositAmount,
  getMinimumWithdrawAmount,
  HEALTH_FACTOR_SCALE,
  type Chain as LiquidiumChain,
  type Pool,
} from '@liquidium/client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LiquidiumClient, { LIQUIDIUM_SIGNING_CHAIN } from '../clients/LiquidiumClient'
import { Chain, PlugToken } from '../interfaces/common'
import { logger } from '../logger'
import { TokenBalance } from '../providers/BalanceProvider'
import { GasCalculator } from '../utils/GasCalculator'
import { formatNumber } from '../utils/formatNumber'
import { decimalsToE8s, e8sToDecimals } from '../utils/validationHelper'
import useBalances from './useBalances'
import useConfig from './useConfig'
import useLiquidium from './useLiquidium'
import useWallet from './useWallet'

/**
 * Yusan needed eight modes because funds had to pass through an internal balance:
 * deposit into it, supply from it, withdraw back to it, take it out again, plus a
 * separate pair for liquidation pools. Liquidium acts on the pool directly, so only
 * the four real operations remain.
 */
export type LiquidiumMode = 'supply' | 'withdraw' | 'borrow' | 'repay'

/**
 * Supplies and repayments are inflows: a transfer to a deposit account, credited a
 * few minutes later. Borrows and withdrawals are outflows, authorized by an
 * Ethereum signature that costs a chain-fusion canister call — measured around nine
 * seconds — so those need visible progress rather than an instant result.
 */
export const isInflow = (mode: LiquidiumMode) => mode === 'supply' || mode === 'repay'

/**
 * Protocol minimums in base units, mirrored from the SDK's own tables. The SDK
 * enforces these too, but only once submitted; checking here lets the button explain
 * itself instead of failing after the fact.
 */
const BPS = 10_000n

/**
 * Margin held back from the borrow maximum, in basis points.
 *
 * Borrowing the full reported capacity fails: it sets LTV exactly at the ceiling and
 * the protocol re-evaluates against freshly accrued interest and a possibly moved
 * collateral price. 0.5% is enough to absorb both without visibly shortchanging the
 * max button. The SDK applies the same idea in the other direction for repayments,
 * where `getMaxRepayAmount` adds a 10bps buffer by default.
 */
const BORROW_HEADROOM_BPS = 50n

/** Fixed-point scale for prices, which arrive as floats. Six places is ample. */
const PRICE_SCALE = 1_000_000n

/**
 * Everything currently owed on a position, unbuffered.
 *
 * `borrowed` alone, and deliberately not `borrowed + debtInterest`. It maps from the
 * canister's `debt_native_now`, which is already the debt at the latest borrow index
 * — interest included. `debtInterest` is a separate cumulative reporting figure, so
 * adding it counts the same interest twice.
 *
 * The SDK states this rule for the mirror-image case and then breaks it here:
 * `getFullWithdrawAmount` returns `deposited` alone and documents "do not add
 * `earnedInterest` to this amount", while `getMaxRepayAmount` computes
 * `borrowed + debtInterest`. Both fields are mapped the same way
 * (`*_native_now` plus a `total_* + *_since_snapshot` companion), so the withdraw
 * side is the consistent one — and Liquidium's own app quotes repayments this way.
 */
const owedOn = (position?: { borrowed: bigint }) => position?.borrowed ?? 0n

/**
 * How far ahead a full repayment covers interest.
 *
 * A repayment has to outrun accrual, which is a rate times a duration — so the margin
 * is a duration, not a fixed percentage. `getMaxRepayAmount` takes basis points
 * instead, and its 10bps default was two thousand times larger than a day of interest
 * on this pool while being far too small for a high-rate one.
 *
 * These are the SDK's own numbers: it applies exactly this rule in
 * `calculateInterestBufferAmount`, but only inside the Simple Loans module, and
 * nothing exposes it. Checked against Liquidium's own app on the same position — its
 * quote of 2.000245 is the debt plus the 227 base units this produces.
 */
const REPAYMENT_BUFFER_SECONDS = 86_400n
const SECONDS_PER_YEAR = 31_536_000n

/**
 * Interest the debt will accrue over the buffer window, in base units.
 *
 * Mirrors the SDK's `calculateInterestBufferAmount`, including its floor of one unit
 * so that a position too small to accrue a whole unit still repays clear.
 */
const interestBufferFor = (debt: bigint, pool?: Pool) => {
  if (debt <= 0n || !pool || pool.borrowingRate <= 0n) return 0n

  const buffer =
    (debt * pool.borrowingRate * REPAYMENT_BUFFER_SECONDS) /
    10n ** pool.rateDecimals /
    SECONDS_PER_YEAR

  return buffer > 1n ? buffer : 1n
}

/**
 * Protocol minimum for an operation, in the asset's base units.
 *
 * Taken from the SDK's own tables rather than a mirrored copy, because they differ
 * per operation in ways a hand-kept table gets wrong: a BTC withdrawal bottoms out
 * at 5,000 sats where a deposit needs 5,100, and ICP has a deposit minimum but no
 * borrow or withdraw one. Assets with no configured minimum return 0n.
 *
 * Repayment has no floor of its own — a debt smaller than the deposit minimum still
 * has to be closable.
 */
const minimumFor = (mode: LiquidiumMode, asset: string) => {
  switch (mode) {
    case 'supply':
      return getMinimumDepositAmount(asset)
    case 'borrow':
      return getMinimumBorrowAmount(asset)
    case 'withdraw':
      return getMinimumWithdrawAmount(asset)
    case 'repay':
      return 0n
  }
}

/**
 * @param preferredAsset Liquidium asset to open on, e.g. from tapping a position.
 *   Falls back to the first available route when the asset has none.
 */
export const useLiquidiumAction = (mode: LiquidiumMode, preferredAsset?: string) => {
  const { t } = useTranslation()
  const { wallet } = useWallet()
  const { tokens, evmChains } = useConfig()
  const { findBalance } = useBalances()
  const { profileId, pools, routes, reserves, summary, prices, refresh } = useLiquidium()

  /** Liquidium's ETH-side pools live on Ethereum mainnet specifically. */
  const ethereumMainnet = useMemo(() => evmChains.find((chain) => chain.chainId === 1), [evmChains])

  const [amount, setAmount] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<boolean | null>(null)
  /** Set while waiting on the Ethereum signature, which is the slow part. */
  const [signing, setSigning] = useState(false)

  /**
   * Most-spendable amount on the native Bitcoin route.
   *
   * Cannot be derived from the wallet balance the way the other routes are. That
   * figure counts sats locked in inscriptions, which are not spendable, and the cost
   * of a Bitcoin transaction depends on the current fee rate and how many inputs it
   * takes — not the flat per-token fee an ICRC ledger charges. `estimateMaxSendable`
   * accounts for both: it excludes inscription UTXOs and subtracts a fee sized for
   * the inputs actually needed.
   *
   * Undefined while it resolves, which leaves the max unavailable rather than
   * briefly wrong.
   */
  const [btcMaxSendable, setBtcMaxSendable] = useState<bigint>()

  /**
   * Full repayment amount, from the protocol.
   *
   * Fetched rather than derived from `reserves`. It depends only on the profile and
   * the pool, not on what has been typed, so this is one query per pool selection —
   * and it reads the debt as it stands now, where the cached reserves are as stale as
   * the provider's last load. Interest accrues the whole time, and that staleness is
   * precisely what leaves dust behind.
   *
   * `getMaxRepayAmount` also owns the buffer that covers interest accruing between
   * quoting and crediting, so the SDK stays the single authority on what "repay
   * everything" means.
   */
  const [maxRepayable, setMaxRepayable] = useState<bigint>()

  /**
   * The wallet's address on a given chain: where an inflow is sent from, and where an
   * outflow is paid out to.
   */
  const addressForRoute = useCallback(
    (chain: LiquidiumChain) => {
      switch (chain) {
        case 'BTC':
          return wallet.btcAddress
        case 'ETH':
          return wallet.ethAddress
        default:
          return wallet.principalId
      }
    },
    [wallet.btcAddress, wallet.ethAddress, wallet.principalId]
  )

  /**
   * Every route the wallet can actually use, in either direction.
   *
   * Outflows were once restricted to the ICP side on the assumption that borrowed and
   * withdrawn funds had to come back through the wallet's own principal. They do not:
   * `lending.borrow` and `lending.withdraw` both take a destination `chain` and
   * `receiver`, so a borrow can be paid out as native BTC or on Ethereum just as a
   * supply can be sent from there. Restricting it meant native-only assets had no
   * borrow option at all.
   *
   * A route is offered only when the wallet has an address on that chain, so a
   * missing one drops the route rather than failing at submit.
   *
   * When opened from a position the list narrows to that asset's rails — repaying a
   * USDT debt offers USDT and ckUSDT, and nothing else. The action belongs to the
   * position that was tapped, so letting the picker roam across every market meant
   * arriving at "Repay USDT" and being able to repay BTC instead. Entering from the
   * top-level Supply and Borrow buttons passes no asset and still offers everything.
   */
  const availableTokens = useMemo(() => {
    const usable = routes.filter((entry) => !!addressForRoute(entry.route))
    const scoped = preferredAsset
      ? usable.filter((entry) => entry.pool.asset === preferredAsset)
      : usable

    // Falling back to the full list keeps the screen usable if the asset turns out to
    // have no route at all, rather than leaving an empty picker and no way forward.
    return (scoped.length ? scoped : usable).map((entry) => entry.token)
  }, [routes, addressForRoute, preferredAsset])

  const [selectedToken, setSelectedToken] = useState<PlugToken | undefined>()

  /**
   * Settles on a token once the routes have loaded, and re-settles if the selected
   * one stops being available.
   *
   * The initial value cannot come from `useState(availableTokens[0])`: routes arrive
   * from the provider asynchronously, so on the first render there is nothing to pick
   * and the initialiser never runs again — which left the screen reporting itself as
   * still loading.
   *
   * No asset preference is applied here because `availableTokens` is already narrowed
   * to it, so the first entry is the right one either way.
   */
  useEffect(() => {
    if (selectedToken && availableTokens.some((token) => token.id === selectedToken.id)) return
    setSelectedToken(availableTokens[0])
  }, [availableTokens, selectedToken])

  /**
   * The chosen token identifies both the pool and the route into it, since a token is
   * only ever one asset on one chain.
   */
  const selectedRoute = useMemo(
    () => routes.find((entry) => entry.token.id === selectedToken?.id),
    [routes, selectedToken]
  )

  /**
   * The pool a token draws on.
   *
   * Routes answer this for every rail, native included; the asset tag is a fallback
   * for a token the routes list has no entry for, which is how a pool still resolves
   * when the token is tagged but its rail is not one the protocol serves.
   */
  const poolForToken = useCallback(
    (token: PlugToken): Pool | undefined =>
      routes.find((entry) => entry.token.id === token.id)?.pool ??
      pools.find((pool) => pool.asset === LiquidiumClient.assetOf(token)),
    [routes, pools]
  )

  const selectedPool = useMemo(
    () => (selectedToken ? poolForToken(selectedToken) : undefined),
    [selectedToken, poolForToken]
  )

  /** Chain the funds travel over, in whichever direction they are moving. */
  const route: LiquidiumChain = selectedRoute?.route ?? 'ICP'

  const selectedReserve = useMemo(
    () => reserves.find((entry) => entry.reserve.pool.id === selectedPool?.id),
    [reserves, selectedPool]
  )

  /** Rate for the side being acted on, as a percentage. */
  const rate = useMemo(() => {
    if (!selectedPool) return undefined
    // By which side of the position the action moves, not by direction of funds:
    // repaying is an inflow but pays down debt, so it carries the borrow rate.
    const touchesDebt = mode === 'borrow' || mode === 'repay'
    const raw = touchesDebt ? selectedPool.borrowingRate : selectedPool.lendingRate
    return (Number(raw) / 10 ** Number(selectedPool.rateDecimals)) * 100
  }, [selectedPool, mode])

  const decimals = selectedToken?.decimals ?? Number(selectedPool?.decimals ?? 8)

  /**
   * The profile's risk aggregates, rebuilt from the per-reserve rows.
   *
   * Liquidium exposes these on the summary, but only as they stand — there is no
   * endpoint that projects them for a hypothetical action, and `quote.calculateLtv`
   * covers a single collateral/borrow pair for Simple Loans rather than a portfolio.
   * Recomputing them here is what makes a projection possible, and it is exact:
   * checked against a live profile, collateral-weighting the per-pool parameters
   * reproduces the canister's `weightedLiquidationThresholdBps`,
   * `weightedMaxLtvBps` and `maxBorrowableUsd` to the unit.
   *
   * Kept as weighted sums rather than as the averaged basis points the summary
   * reports, because those are truncated to whole bps and the rounding shows up as a
   * visible drift once it is multiplied back out against the collateral.
   *
   * All USD figures carry `summary.usdDecimals` (27) places.
   */
  const riskWeights = useMemo(
    () =>
      reserves.reduce(
        (acc, entry) => ({
          // USD × bps.
          weightedThreshold:
            acc.weightedThreshold +
            entry.reserve.suppliedUsd * entry.reserve.pool.liquidationThreshold,
          weightedMaxLtv:
            acc.weightedMaxLtv + entry.reserve.suppliedUsd * entry.reserve.pool.maxLtv,
          debt: acc.debt + entry.reserve.borrowedUsd,
        }),
        { weightedThreshold: 0n, weightedMaxLtv: 0n, debt: 0n }
      ),
    [reserves]
  )

  /** Converts a base-unit amount of the selected asset into 27-decimal USD. */
  const toUsd = useMemo(() => {
    const price = selectedPool ? prices[selectedPool.asset] : undefined
    const scaledPrice = price ? BigInt(Math.round(price * Number(PRICE_SCALE))) : 0n
    if (!summary || scaledPrice <= 0n) return undefined

    return (baseUnits: bigint) =>
      (baseUnits * scaledPrice * 10n ** summary.usdDecimals) /
      (PRICE_SCALE * 10n ** BigInt(decimals))
  }, [selectedPool, prices, summary, decimals])

  /** The inverse, for turning a USD ceiling back into something the user can enter. */
  const fromUsd = useMemo(() => {
    const price = selectedPool ? prices[selectedPool.asset] : undefined
    const scaledPrice = price ? BigInt(Math.round(price * Number(PRICE_SCALE))) : 0n
    if (!summary || scaledPrice <= 0n) return undefined

    return (usd: bigint) =>
      (usd * 10n ** BigInt(decimals) * PRICE_SCALE) / (scaledPrice * 10n ** summary.usdDecimals)
  }, [selectedPool, prices, summary, decimals])

  /**
   * The ceiling differs per mode, and only two of them relate to the wallet:
   * supplying and repaying spend held tokens, while withdrawing and borrowing draw
   * on the position and the protocol's limits.
   */
  const maxAmount = useMemo(() => {
    if (!selectedToken || !selectedPool) return undefined

    const walletBalance = findBalance({ id: selectedToken.id })?.balance ?? 0n
    // Both inflows are ledger transfers, so the ledger fee has to stay behind.
    const spendable = walletBalance > selectedToken.fee ? walletBalance - selectedToken.fee : 0n

    // Native Bitcoin has its own ceiling: inscription-locked sats are not spendable
    // and the mining fee is not a flat per-token amount.
    const inflowSpendable = route === 'BTC' ? btcMaxSendable : spendable

    switch (mode) {
      case 'supply': {
        if (inflowSpendable === undefined) return undefined
        return inflowSpendable
      }
      case 'repay': {
        if (inflowSpendable === undefined || maxRepayable === undefined) return undefined
        return inflowSpendable < maxRepayable ? inflowSpendable : maxRepayable
      }
      case 'withdraw': {
        const deposited = selectedReserve?.reserve.position.deposited ?? 0n
        // Collateral is only freely withdrawable while nothing is borrowed against it.
        if (riskWeights.debt === 0n || !fromUsd) return deposited

        // Withdrawing lowers borrowing power by the asset's own max LTV, and the
        // protocol rejects anything that would leave the debt above what remains.
        // Without this the max button offered the whole deposit and the transaction
        // failed at the canister.
        const spareUsdBps = riskWeights.weightedMaxLtv - riskWeights.debt * BPS
        if (spareUsdBps <= 0n || selectedPool.maxLtv <= 0n) return 0n

        // Same headroom as the borrow ceiling, for the same reason: interest accrues
        // and prices move between quoting this and the protocol re-checking it.
        const withdrawableUsd =
          (spareUsdBps * (BPS - BORROW_HEADROOM_BPS)) / (selectedPool.maxLtv * BPS)

        const byHealth = fromUsd(withdrawableUsd)
        return byHealth < deposited ? byHealth : deposited
      }
      case 'borrow': {
        if (!summary || !fromUsd) return 0n

        // `availableBorrowsUsd` is `maxBorrowableUsd - debt`, so spending all of it
        // puts LTV exactly on the ceiling — which the protocol rejects with
        // MAX_LTV_EXCEEDED once accrued interest and a moved price are applied. Hold
        // back a margin so the max button yields something actually borrowable.
        const usableUsd = (summary.availableBorrowsUsd * (BPS - BORROW_HEADROOM_BPS)) / BPS

        // fromUsd stays in bigint throughout. These USD values carry 27 decimals, so
        // routing them through Number loses precision far above MAX_SAFE_INTEGER and
        // can round up into the very overshoot this is avoiding.
        const byPower = fromUsd(usableUsd)

        return byPower < selectedPool.availableLiquidity ? byPower : selectedPool.availableLiquidity
      }
    }
  }, [
    mode,
    route,
    btcMaxSendable,
    maxRepayable,
    selectedToken,
    selectedPool,
    selectedReserve,
    summary,
    riskWeights,
    fromUsd,
    findBalance,
  ])

  useEffect(() => {
    const btcAddress = wallet.btcAddress
    if (!isInflow(mode) || route !== 'BTC' || !btcAddress) {
      setBtcMaxSendable(undefined)
      return
    }

    let cancelled = false

    new GasCalculator()
      .estimateMaxUTXOSendable(btcAddress, Chain.BTC)
      .then(({ maxAmount }) => {
        if (!cancelled) setBtcMaxSendable(maxAmount)
      })
      .catch((e) => {
        logger.debug(`Liquidium: could not estimate max BTC: ${String(e)}`)
        if (!cancelled) setBtcMaxSendable(undefined)
      })

    // The route or address can change while the estimate is in flight; a late reply
    // for a route the user has moved on from would show the wrong ceiling.
    return () => {
      cancelled = true
    }
  }, [mode, route, wallet.btcAddress])

  useEffect(() => {
    if (mode !== 'repay' || !profileId || !selectedPool) {
      setMaxRepayable(undefined)
      return
    }

    let cancelled = false

    // `getPosition` rather than `getMaxRepayAmount`: that helper double-counts
    // interest, and its buffer is a flat percentage where the exposure is a rate
    // over time. Both corrections are applied here instead.
    new LiquidiumClient().positions
      .getPosition(profileId, selectedPool.id)
      .then((position) => {
        if (cancelled) return
        const owed = owedOn(position ?? undefined)
        setMaxRepayable(owed + interestBufferFor(owed, selectedPool))
      })
      .catch((e) => {
        // Falling back to the cached reserve keeps repayment possible when the query
        // fails. Repaying is the action that protects a position from liquidation, so
        // a slightly stale ceiling is far better than no way to repay at all.
        logger.debug(`Liquidium: could not fetch max repay: ${String(e)}`)
        if (cancelled) return

        const owed = owedOn(selectedReserve?.reserve.position)
        setMaxRepayable(owed + interestBufferFor(owed, selectedPool))
      })

    // The pool can change while the query is in flight; a late reply for an asset the
    // user has moved on from would cap the wrong position.
    return () => {
      cancelled = true
    }
  }, [mode, profileId, selectedPool, selectedReserve])

  const amountBaseUnits = useMemo(
    () => (amount ? BigInt(decimalsToE8s(amount, decimals)) : 0n),
    [amount, decimals]
  )

  /**
   * The position as it stands, and as this action would leave it.
   *
   * Both sides are derived from the same reserve rows so that a zero amount projects
   * to exactly the current value — mixing the canister's reported health factor with
   * a locally derived projection would show a phantom impact before the user has
   * typed anything, since the two come from separate calls a moment apart.
   *
   * Health factor is `collateral × weightedLiquidationThreshold / debt`, and
   * borrowing power is `collateral × weightedMaxLtv`; both weightings shift when the
   * amount changes, which is why this re-weights per reserve rather than reusing the
   * summary's averages.
   */
  const projection = useMemo(() => {
    /** Undefined health means no debt — maximum safety, not zero. */
    const factor = (weightedThreshold: bigint, debt: bigint) =>
      debt > 0n
        ? Number((weightedThreshold * HEALTH_FACTOR_SCALE) / (BPS * debt)) /
          Number(HEALTH_FACTOR_SCALE)
        : undefined

    const current = factor(riskWeights.weightedThreshold, riskWeights.debt)

    if (!selectedPool || !toUsd || amountBaseUnits <= 0n) {
      return { current, projected: current, wouldExceedMaxLtv: false }
    }

    const deltaUsd = toUsd(amountBaseUnits)
    // Amounts are clamped at zero: repaying or withdrawing slightly more than the
    // recorded balance is normal — the max buttons quote a moment-old figure — and
    // should read as "cleared", not as a negative position.
    const drop = (value: bigint, delta: bigint) => (value > delta ? value - delta : 0n)

    // Supplying or borrowing an asset the profile has never touched has no reserve
    // row, so the change has to be applied against a zero baseline instead.
    const suppliedUsd = selectedReserve?.reserve.suppliedUsd ?? 0n
    const borrowedUsd = selectedReserve?.reserve.borrowedUsd ?? 0n

    const suppliedAfter =
      mode === 'supply'
        ? suppliedUsd + deltaUsd
        : mode === 'withdraw'
          ? drop(suppliedUsd, deltaUsd)
          : suppliedUsd
    const borrowedAfter =
      mode === 'borrow'
        ? borrowedUsd + deltaUsd
        : mode === 'repay'
          ? drop(borrowedUsd, deltaUsd)
          : borrowedUsd

    // Only the selected pool's contribution changes, so the rest of the weighted sums
    // carry over untouched.
    const suppliedDelta = suppliedAfter - suppliedUsd
    const weightedThreshold =
      riskWeights.weightedThreshold + suppliedDelta * selectedPool.liquidationThreshold
    const weightedMaxLtv = riskWeights.weightedMaxLtv + suppliedDelta * selectedPool.maxLtv
    const debt = riskWeights.debt + (borrowedAfter - borrowedUsd)

    return {
      current,
      projected: factor(weightedThreshold, debt),
      // The condition the protocol itself enforces on an outflow: debt has to stay
      // within the borrowing power the remaining collateral supports.
      wouldExceedMaxLtv: debt * BPS > weightedMaxLtv,
    }
  }, [riskWeights, selectedPool, selectedReserve, toUsd, amountBaseUnits, mode])

  const error = useMemo(() => {
    if (!profileId) return t('borrowLend.errors.loading')
    if (!selectedToken || !selectedPool) return t('borrowLend.errors.loading')
    if (!amount || amountBaseUnits <= 0n) return t('borrowLend.errors.noAmount')

    if (selectedPool.frozen) return t('borrowLend.errors.loading')

    // The native Bitcoin ceiling is fetched, not derived. Until it arrives there is
    // nothing to validate against, and letting the amount through would mean a send
    // that fails once the fee cannot be covered.
    if (isInflow(mode) && route === 'BTC' && btcMaxSendable === undefined) {
      return t('borrowLend.errors.loading')
    }

    // Same reasoning for the repayment ceiling, which the protocol supplies: until it
    // arrives there is nothing to validate against, and a repayment sized against a
    // guess is what leaves dust.
    if (mode === 'repay' && maxRepayable === undefined) {
      return t('borrowLend.errors.loading')
    }

    if (amountBaseUnits < minimumFor(mode, selectedPool.asset)) {
      return t('borrowLend.errors.amountTooSmall')
    }

    if (maxAmount !== undefined && amountBaseUnits > maxAmount) {
      return mode === 'borrow'
        ? t('borrowLend.errors.notEnoughSupply')
        : t('borrowLend.errors.notEnoughBalance')
    }

    // Only outflows can undercollateralize a position; supplying and repaying can
    // only improve it. The ceilings above already hold this line, so reaching here
    // means the position moved underneath them — worth catching before the protocol
    // rejects the transaction.
    if (!isInflow(mode) && projection.wouldExceedMaxLtv) {
      return t('borrowLend.errors.exceedsMaxLtv')
    }

    return undefined
  }, [
    profileId,
    selectedToken,
    selectedPool,
    amount,
    amountBaseUnits,
    maxAmount,
    mode,
    route,
    btcMaxSendable,
    maxRepayable,
    projection,
    t,
  ])

  const properties = useMemo(() => {
    const rows: { title: string; value?: string }[] = []

    // The health factor is deliberately not listed here: the portfolio card above
    // already shows the same change as a bar, and stating it twice made the raw
    // factor compete with the percentage for the reader's attention.

    if (rate !== undefined) {
      rows.push({
        title: mode === 'supply' || mode === 'withdraw' ? t('borrowLend.apy') : t('borrowLend.apr'),
        value: `${rate.toFixed(2)}%`,
      })
    }

    if (isInflow(mode)) {
      // Measured on a real deposit: the protocol pays a second ledger fee sweeping
      // funds out of the deposit account, so slightly less is credited than sent.
      rows.push({
        title: t('borrowLend.creditTime'),
        value: t('borrowLend.creditTimeValue'),
      })
    }

    if (mode === 'repay' && selectedReserve) {
      rows.push({
        title: t('borrowLend.debt'),
        // Every place the asset can express, rather than formatNumber's usual four.
        // The debt is the figure the Max button is derived from, so rounding it to
        // "2 USDC" beside a max of 2.000246 read as a mismatch rather than as
        // rounding. The significant-digit options are cleared so a sub-0.001 debt
        // gets the same treatment instead of collapsing to two digits.
        value: formatNumber(
          e8sToDecimals(owedOn(selectedReserve.reserve.position), decimals),
          selectedToken?.symbol,
          {
            maximumFractionDigits: decimals,
            minimumSignificantDigits: undefined,
            maximumSignificantDigits: undefined,
          }
        ),
      })
    }

    return rows
  }, [rate, mode, selectedToken, selectedReserve, decimals, projection, t])

  /**
   * Balance to show against each selectable token. What counts as "your balance"
   * depends on the operation: supplying and repaying spend held tokens, while
   * withdrawing draws on what the position holds. Borrowing has no meaningful
   * per-token balance, so the screen hides it entirely.
   */
  const tokensToBalances = (candidates: PlugToken[]): TokenBalance[] =>
    candidates.map((token) => {
      const pool = poolForToken(token)
      const reserve = reserves.find((entry) => entry.reserve.pool.id === pool?.id)

      const balance =
        mode === 'withdraw'
          ? (reserve?.reserve.position.deposited ?? 0n)
          : (findBalance({ id: token.id })?.balance ?? 0n)

      return {
        token,
        balance,
        humanBalance: e8sToDecimals(balance, token.decimals),
      } satisfies TokenBalance
    })

  const onSubmit = async () => {
    if (!profileId || !selectedPool || !selectedToken) throw Error('Not ready')

    // The wallet's own address on the route: the source for an inflow, the
    // destination for an outflow. `availableTokens` only offers routes this resolves
    // for, so reaching here without one means the wallet changed under the screen.
    const sendingAccount = addressForRoute(route)
    if (!sendingAccount) {
      throw Error(`This wallet has no ${route} address`)
    }

    setSubmitting(true)
    setSuccess(null)

    const client = new LiquidiumClient(wallet.principalId, {
      wallet,
      evmChain: ethereumMainnet,
    })
    const walletAdapter = client.getWalletAdapter()

    try {
      if (isInflow(mode)) {
        await client.lending.supply({
          profileId,
          poolId: selectedPool.id,
          action: mode === 'supply' ? 'deposit' : 'repayment',
          chain: route,
          walletAdapter,
          // The sending account differs per route: an ICRC transfer comes from the
          // principal, a native one from that chain's address.
          account: sendingAccount,
          amount: amountBaseUnits,
        })
      } else {
        // Authorization is always an Ethereum signature regardless of where the funds
        // are paid out; ETH is the only chain Plug can sign Liquidium messages with.
        const signerWalletAddress = wallet.ethAddress
        if (!signerWalletAddress) {
          throw Error('This wallet has no Ethereum address, so it cannot authorize this')
        }

        setSigning(true)
        const request = {
          profileId,
          poolId: selectedPool.id,
          amount: amountBaseUnits,
          // Paid out on the route the selected token represents, to this wallet's
          // address on that chain.
          chain: route,
          receiver: sendingAccount,
          signerWalletAddress,
          signerChain: LIQUIDIUM_SIGNING_CHAIN,
          signerWalletAdapter: walletAdapter,
        }

        if (mode === 'borrow') await client.lending.borrow(request)
        else await client.lending.withdraw(request)
      }

      setSuccess(true)
      setAmount('')
      refresh()
    } catch (e) {
      setSuccess(false)
      throw e
    } finally {
      setSigning(false)
      setSubmitting(false)
    }
  }

  return {
    mode,
    amount,
    setAmount,
    selectedToken,
    setSelectedToken,
    availableTokens,
    selectedPool,
    maxAmount,
    rate,
    /** Current and post-action health factor; `undefined` on either side means no debt. */
    healthFactorImpact: projection,
    properties,
    error,
    submitting,
    signing,
    success,
    onSubmit,
    tokensToBalances,
  }
}
